import { TitleCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { LocalDate, LocalDateTime, Month, TemporalAdjusters } from 'js-joda';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

export interface MonthYearTimestamp {
  monthYear: string;
  startTimestamp: LocalDateTime;
  endTimestamp: LocalDateTime;
}

export interface YearTimestamp {
  year: string;
  startMonth: LocalDate;
  startTimestamp: LocalDateTime;
  endMonth: LocalDate;
  endTimestamp: LocalDateTime;
}

function memoize<T extends (...args: any[]) => any>(func: T): T {
  const cache = new Map<string, any>();
  return function(this: any, ...args: any[]) {
    const key = JSON.stringify(args);
    if (!cache.has(key)) {
      cache.set(key, func.apply(this, args));
    }
    return cache.get(key);
  } as T;
}

@Injectable({
  providedIn: 'root'
})
export class DateService {
  private static readonly numberOfYears = 2;
  private static readonly numberOfMonths = 12;

  private readonly now$ = new BehaviorSubject<LocalDate>(LocalDate.now());

  readonly yearsWithTimestamp$: Observable<YearTimestamp[]> = this.now$.pipe(
    map(now => this.memoizedGetYearsWithTimestamp(now)),
    shareReplay(1)
  );
  readonly monthsWithTimestamp$: Observable<MonthYearTimestamp[]> = this.now$.pipe(
    map(now => this.memoizedGetMonthsWithTimestamp(now)),
    shareReplay(1)
  );

  private memoizedGetYearTimestamp = memoize((date: LocalDate): YearTimestamp => {
    const year = date.year().toString();
    const startMonth = date.withMonth(Month.JANUARY);
    const startTimestamp = this.toLocalDateTime(startMonth.with(TemporalAdjusters.firstDayOfMonth()));
    const endMonth = date.withMonth(Month.DECEMBER);
    const endTimestamp = this.toLocalDateTime(endMonth.with(TemporalAdjusters.lastDayOfMonth()))
      .plusDays(1)
      .minusSeconds(1);
    return { year, startMonth, startTimestamp, endMonth, endTimestamp };
  });

  private memoizedGetMonthYearTimestamp = memoize((date: LocalDate): MonthYearTimestamp => {
    const firstDayofMonth = date.with(TemporalAdjusters.firstDayOfMonth());
    const lastDayOfMonth = date.with(TemporalAdjusters.lastDayOfMonth());
    const startTimestamp = this.toLocalDateTime(firstDayofMonth);
    const endTimestamp = this.toLocalDateTime(lastDayOfMonth)
      .plusDays(1)
      .minusSeconds(1);
    const monthYear = this.getMonthYearLabel(firstDayofMonth);
    return { monthYear, startTimestamp, endTimestamp };
  });

  private memoizedGetYearsWithTimestamp = memoize((fromDate: LocalDate): YearTimestamp[] => {
    const yearsWithTimestamp: YearTimestamp[] = [];
    for (let offsetYears = 1; offsetYears <= DateService.numberOfYears; offsetYears++) {
      const date = fromDate.minusYears(offsetYears);
      const year: YearTimestamp = this.memoizedGetYearTimestamp(date);
      yearsWithTimestamp.push(year);
    }
    return yearsWithTimestamp;
  });

  private memoizedGetMonthsWithTimestamp = memoize((fromDate: LocalDate): MonthYearTimestamp[] => {
    const monthsWithTimestamp: MonthYearTimestamp[] = [];
    for (
      let offsetMonths = 1;
      offsetMonths <= DateService.numberOfMonths;
      offsetMonths++
    ) {
      const date = fromDate.minusMonths(offsetMonths);
      const month: MonthYearTimestamp = this.memoizedGetMonthYearTimestamp(date);
      monthsWithTimestamp.push(month);
    }
    return monthsWithTimestamp;
  });

  constructor(private titleFormatter: TitleCasePipe) {
    /*
      No need to initialize directly here — the Observables will emit values.
      If you need the values immediately in the constructor, you can subscribe (with caution).
      However, ideally, components that *consume* these values should handle the subscription.
    */
  }

  getYearsWithTimestamp(fromDate: LocalDate): YearTimestamp[] {
    return this.memoizedGetYearsWithTimestamp(fromDate);
  }

  getMonthsWithTimestamp(fromDate: LocalDate): MonthYearTimestamp[] {
    return this.memoizedGetMonthsWithTimestamp(fromDate);
  }

  toLocalDateTime(date: LocalDate): LocalDateTime {
    return LocalDateTime.of(date.year(), date.month(), date.dayOfMonth(), 0, 0);
  }

  getMonthYearLabel(date: LocalDate): string {
    return `${this.titleFormatter.transform(
      date.month().name()
    )} ${date.year().toString()}`;
  }

  getYearTimestamp(date: LocalDate): YearTimestamp {
    return this.memoizedGetYearTimestamp(date);
  }

  getMonthYearTimestamp(date: LocalDate): MonthYearTimestamp {
    return this.memoizedGetMonthYearTimestamp(date);
  }
}
